/**
 * Input Type Detection Hook
 *
 * Pure detection: takes input value and transcription flag,
 * returns detected type with label, icon name, and extracted URLs.
 * Mirrors matchScenario() logic in useDemoPipeline.ts so
 * the badge matches pipeline behavior.
 */

import { useMemo } from 'react';

type InputType = 'TEXT' | 'LINK' | 'AUDIO';

/** Regex to find URLs anywhere in text (not just at the start) */
const URL_REGEX = /https?:\/\/[^\s<>"')\]]+/gi;

export interface DetectedInput {
  /** Pipeline input type */
  type: InputType;
  /** Human-readable label for the badge */
  label: string;
  /** Lucide icon name to render */
  icon: 'type' | 'link-2' | 'mic';
  /** URLs found in the input (always populated) */
  extractedUrls: string[];
}

export function useInputTypeDetection(
  value: string,
  wasTranscribed: boolean
): DetectedInput {
  return useMemo(() => {
    const trimmed = value.trim();
    if (!trimmed) {
      return { type: 'TEXT', label: 'Text', icon: 'type' as const, extractedUrls: [] };
    }

    // Voice input flag set by speech recognition
    if (wasTranscribed) {
      return { type: 'AUDIO', label: 'Voice input', icon: 'mic' as const, extractedUrls: [] };
    }

    // Extract all URLs from the input
    const urlMatches = trimmed.match(URL_REGEX) || [];

    if (urlMatches.length > 0) {
      // Entire input is a single URL
      if (urlMatches.length === 1 && trimmed === urlMatches[0]) {
        return {
          type: 'LINK',
          label: 'URL detected',
          icon: 'link-2' as const,
          extractedUrls: urlMatches,
        };
      }

      // Text contains embedded URLs
      return {
        type: 'TEXT',
        label: `Text + ${urlMatches.length} link${urlMatches.length > 1 ? 's' : ''}`,
        icon: 'link-2' as const,
        extractedUrls: urlMatches,
      };
    }

    return { type: 'TEXT', label: 'Text', icon: 'type' as const, extractedUrls: [] };
  }, [value, wasTranscribed]);
}
